import React from 'react';
import { Loader, Pagination } from '@mantine/core';
import { LOG_LEVEL_LABELS, LOG_PLACE_LABELS, resolveStatusClass } from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type ErrorsTableProps = Pick<
  UseMonitoringPage,
  | 'errorRows'
  | 'isErrorLoading'
  | 'hasLoadedErrors'
  | 'errorTotalPages'
  | 'errorPage'
  | 'setErrorPage'
  | 'openErrorDetail'
>;

const resolvePlaceLabel = (place: unknown): string => {
  if (place === 'backend') return LOG_PLACE_LABELS.backend;
  if (place === 'frontend') return LOG_PLACE_LABELS.frontend;
  return '-';
};

const resolveLevelLabel = (level: unknown): string => {
  if (level === 'error') return LOG_LEVEL_LABELS.error;
  if (level === 'warn') return LOG_LEVEL_LABELS.warn;
  if (level === 'info') return LOG_LEVEL_LABELS.info;
  if (level === 'debug') return LOG_LEVEL_LABELS.debug;
  return '-';
};

const placeBadgeClass = (place: unknown) =>
  place === 'backend' ? classes.placeBadgeBackend : classes.placeBadgeFrontend;

const levelBadgeClass = (level: unknown) =>
  level === 'error' ? classes.levelBadgeError : level === 'warn' ? classes.levelBadgeWarn : classes.levelBadgeMuted;

const statusBadgeClass = (statusClass: '4xx' | '5xx' | null) =>
  statusClass === '5xx' ? classes.statusBadge5xx : statusClass === '4xx' ? classes.statusBadge4xx : classes.levelBadgeMuted;

export const ErrorsTable: React.FC<ErrorsTableProps> = ({
  errorRows,
  isErrorLoading,
  hasLoadedErrors,
  errorTotalPages,
  errorPage,
  setErrorPage,
  openErrorDetail,
}) => {
  if (isErrorLoading && !hasLoadedErrors) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table} data-testid="monitoring-errors-table">
        <thead>
          <tr>
            <th>{'מלצר'}</th>
            <th>{'מכשיר'}</th>
            <th>{'תאריך ושעה'}</th>
            <th>{'משמרת'}</th>
            <th>{'עסק'}</th>
            <th>{'מקור'}</th>
            <th>{'חומרה'}</th>
            <th>{'קוד'}</th>
            <th>{'שגיאה'}</th>
            <th>{'trace'}</th>
          </tr>
        </thead>
        <tbody>
          {errorRows.map((row) => (
            <tr
              key={row.id}
              className={row.isIgnored ? `${classes.clickableRow} ${classes.mutedRow}` : classes.clickableRow}
              role="button"
              tabIndex={0}
              data-testid={`monitoring-errors-row-${row.id}`}
              onClick={() => openErrorDetail(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openErrorDetail(row);
                }
              }}
            >
              <td data-label={'מלצר'}>{row.employeeName ?? '-'}</td>
              <td data-label={'מכשיר'}>{row.deviceId ?? '-'}</td>
              <td data-label={'תאריך ושעה'}>{formatDateTime(row.createdAt)}</td>
              <td data-label={'משמרת'}>{row.sessionLabel ?? '-'}</td>
              <td data-label={'עסק'}>{row.companyName ?? row.tenantId ?? '-'}</td>
              <td data-label={'מקור'}>
                <span className={`${classes.detailKindBadge} ${placeBadgeClass(row.place)}`}>
                  {resolvePlaceLabel(row.place)}
                </span>
              </td>
              <td data-label={'חומרה'}>
                <span className={`${classes.detailKindBadge} ${levelBadgeClass(row.level)}`}>
                  {resolveLevelLabel(row.level)}
                </span>
              </td>
              <td data-label={'קוד'}>
                {row.httpStatus != null ? (
                  <span className={`${classes.detailKindBadge} ${statusBadgeClass(resolveStatusClass(row.httpStatus))}`}>
                    {row.httpStatus}
                  </span>
                ) : (
                  '-'
                )}
              </td>
              <td data-label={'שגיאה'}>
                <div className={classes.errorMessageCell}>
                  {row.errorName ? `${row.errorName}: ${row.message}` : row.message}
                </div>
              </td>
              <td data-label={'trace'}>
                <div className={classes.errorTraceInline}>{row.trace ?? '-'}</div>
              </td>
            </tr>
          ))}
          {errorRows.length === 0 && (
            <tr>
              <td colSpan={10} className={classes.empty}>
                {'אין שגיאות המתאימות לסינון הנוכחי.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {errorTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={errorTotalPages} value={errorPage} onChange={setErrorPage} size="sm" />
        </div>
      )}
    </div>
  );
};
